import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Alert, Box, Button, Chip, Divider, Grid, LinearProgress, Paper, Stack, Table, TableBody,
  TableCell, TableHead, TableRow, TextField, Typography,
} from '@mui/material';
import { api, ApiError } from '../api/client';
import type {
  DeployTokenResult, RefundResult, StepResult, SubscriptionDetail as SubDetail, SubscriptionSettlement,
  SubStatus, TxRequestResult,
} from '../api/types';
import { useAuth } from '../auth/AuthContext';
import { AddressWithCopy, PageHeader, TxHashLink, fmtUnits } from '../components/common';
import { TxPlanStep, TxProgressDialog, TxResult } from '../components/TxProgressDialog';

/** 컨트랙트 배포가 거치는 단계(서버 steps 와 key 대응) */
const DEPLOY_PLAN: TxPlanStep[] = [
  { key: 'deploy', label: '토큰증권 컨트랙트 배포' },
  { key: 'agent:operator', label: 'agent 권한 부여 (운영키)' },
  { key: 'record', label: '공고·토큰 등록(DB)' },
];

const STATUS: Record<SubStatus, { label: string; color: 'info' | 'warning' | 'success' | 'default' | 'error' }> = {
  OPEN: { label: '모집중', color: 'info' },
  CLOSED: { label: '마감', color: 'warning' },
  ISSUED: { label: '발행완료', color: 'success' },
  SETTLED: { label: '정산완료', color: 'success' },
  CANCELLED: { label: '취소(환불)', color: 'error' },
};

/** 청약 운영 단계 — 마감 → 배포 → 발행 → 정산 */
const STEPS: { key: string; label: string; done: (s: SubDetail) => boolean }[] = [
  { key: 'close', label: '① 마감', done: (s) => s.status !== 'OPEN' },
  { key: 'deploy', label: '② 컨트랙트 배포', done: (s) => !!s.tokenContract },
  { key: 'issue', label: '③ 발행·분배', done: (s) => !!s.issueTxHash },
  { key: 'settle', label: '④ 정산', done: (s) => s.status === 'SETTLED' },
];

/**
 * 발행자(ST)·관리자 — 청약 운영 화면 (SFR-003).
 * 마감 → 토큰증권 컨트랙트 배포(관리자) + agent 부여 → 청약자 일괄 발행 → 에스크로 정산/환불.
 */
export function IssuerSubscriptionOps() {
  const { id = '' } = useParams();
  const nav = useNavigate();
  const qc = useQueryClient();
  const { hasRole } = useAuth();

  const sub = useQuery({ queryKey: ['subscription', id], queryFn: () => api.get<SubDetail>(`/subscriptions/${id}`) });
  const settlement = useQuery({
    queryKey: ['subscription', id, 'settlement'],
    queryFn: () => api.get<SubscriptionSettlement>(`/subscriptions/${id}/settlement`),
  });

  const [txTitle, setTxTitle] = useState('');
  const [txOpen, setTxOpen] = useState(false);
  const [pending, setPending] = useState(false);
  const [result, setResult] = useState<TxResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [steps, setSteps] = useState<StepResult[] | null>(null);
  const [plan, setPlan] = useState<TxPlanStep[] | undefined>(undefined);
  const [refundWallet, setRefundWallet] = useState('');
  const [settleAmount, setSettleAmount] = useState('');

  const refresh = () => {
    void qc.invalidateQueries({ queryKey: ['subscription', id] });
    void qc.invalidateQueries({ queryKey: ['subscriptions'] });
  };

  /** 온체인 액션 공통 실행기 — 진행 모달 + 결과/실패 사유 표시 */
  const run = (title: string, fn: () => Promise<TxResult | null>, after?: string) => async () => {
    setTxTitle(title); setTxOpen(true); setPending(true); setResult(null); setError(null); setNotice(null); setSteps(null);
    try {
      const r = await fn();
      setResult(r);
      if (after) setNotice(after);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : `${title} 실패`);
    } finally {
      setPending(false);
      refresh();
    }
  };

  const close = useMutation({
    mutationFn: () => api.post(`/subscriptions/${id}/close`),
    onSuccess: () => { setNotice('청약을 마감했습니다. 다음 단계는 컨트랙트 배포입니다.'); refresh(); },
    onError: (e) => setError(e instanceof ApiError ? e.message : '마감 실패'),
  });

  if (sub.isLoading) return <LinearProgress />;
  if (sub.error || !sub.data) return <Typography color="error">공고를 불러오지 못했습니다.</Typography>;
  const s = sub.data;
  const st = settlement.data;
  const isAdmin = hasRole('admin');

  const deploy = run('토큰증권 컨트랙트 배포', async () => {
    setPlan(DEPLOY_PLAN);
    const r = await api.post<DeployTokenResult>(`/subscriptions/${id}/deploy`);
    setSteps(r.steps ?? null);
    if (r.tokenContract) {
      setNotice(`배포 완료: ${r.tokenContract} · agent 부여 ${r.agentGrants.filter((g) => g.status === 'COMPLETED').length}건`);
    }
    return { status: r.deployTx.status, txHash: r.deployTx.txHash, revertReason: r.deployTx.revertReason };
  });

  const issue = run('토큰증권 발행·분배', async () => {
    const r = await api.post<TxRequestResult>(`/subscriptions/${id}/issue`);
    return { status: r.status, txHash: r.txHash, revertReason: r.revertReason };
  }, '청약자에게 일괄 발행(batchMint)했습니다.');

  const settle = run('청약 대금 정산', async () => {
    const r = await api.post<{ tx: TxRequestResult; issuerWallet: string; amount: string }>(
      `/subscriptions/${id}/settle`, settleAmount ? { amount: settleAmount } : {},
    );
    setNotice(`정산 대상 ${r.issuerWallet} · ${fmtUnits(r.amount)} KRWS`);
    return { status: r.tx.status, txHash: r.tx.txHash, revertReason: r.tx.revertReason };
  });

  const refund = (wallet?: string) => run(wallet ? '청약 환불(지정)' : '청약 환불(전체)', async () => {
    const r = await api.post<RefundResult>(`/subscriptions/${id}/refund`, wallet ? { wallet } : {});
    const ok = r.results.filter((x) => x.status === 'COMPLETED').length;
    const failed = r.results.find((x) => x.status === 'FAILED');
    setNotice(`환불 ${ok}/${r.results.length}건 · 잔여 예치 ${fmtUnits(r.remainingDeposited)} KRWS`);
    return failed
      ? { status: 'FAILED' as const, txHash: failed.txHash, revertReason: failed.reason }
      : { status: 'COMPLETED' as const, txHash: r.results.find((x) => x.txHash)?.txHash };
  });

  return (
    <Box>
      <PageHeader
        crumb={isAdmin ? '관리자 · 청약 운영' : '발행자(ST) · 청약 운영'}
        title={s.tokenName}
        desc="마감 → 컨트랙트 배포 → 발행·분배 → 정산 순으로 진행합니다."
        action={<Button onClick={() => nav('/issuer/subscriptions')}>← 목록</Button>}
      />

      {notice && <Alert severity="info" sx={{ mb: 2 }} onClose={() => setNotice(null)}>{notice}</Alert>}
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

      <Grid container spacing={3}>
        {/* 공고 요약 */}
        <Grid item xs={12} md={5}>
          <Paper sx={{ p: 3 }}>
            <Stack direction="row" alignItems="center" spacing={1} mb={2}>
              <Typography variant="h2">{s.symbol}</Typography>
              <Chip size="small" color={STATUS[s.status].color} label={STATUS[s.status].label} sx={{ fontWeight: 700 }} />
            </Stack>
            <Row k="단가" v={`${fmtUnits(s.unitPrice, 0)} KRWS`} />
            <Row k="총 발행 수량" v={fmtUnits(s.totalAmount, 0)} />
            <Row k="모집 목표" v={`${fmtUnits(s.raiseTarget, 0)} KRWS`} />
            <Row k="청약 기간" v={`${s.periodStart?.slice(0, 10)} ~ ${s.periodEnd?.slice(0, 10)}`} />
            {s.status !== 'OPEN' && <Row k="마감 사유" v={<CloseReasonChip reason={s.closeReason} at={s.closedAt} />} />}
            <Row k="청약자 수" v={`${s.applicantCount}명`} />
            {/* 누계가 아니라 **현재 잔액**이다 — 정산·환불이 끝나면 0 이 된다.
                '예치'로 적으면 아래 청약자별 예치액과 어긋나 보인다. */}
            <Row k="에스크로 잔액" v={`${fmtUnits(st?.totalDeposited ?? s.deposited)} KRWS`} />
            <Divider sx={{ my: 2 }} />
            <Row k="토큰 컨트랙트" v={s.tokenContract ? <AddressWithCopy address={s.tokenContract} /> : '미배포'} />
            <Row k="배포 tx" v={<TxHashLink hash={s.deployTxHash ?? null} />} />
            <Row k="발행 tx" v={<TxHashLink hash={s.issueTxHash && s.issueTxHash !== 'legacy' ? s.issueTxHash : null} />} />
            <Row k="정산 tx" v={<TxHashLink hash={s.settleTxHash ?? null} />} />
            <Row k={s.status === 'SETTLED' ? '정산 수취 지갑' : '정산 예정 지갑'}
              v={st?.issuerWallet ? <AddressWithCopy address={st.issuerWallet} /> : '-'} />
          </Paper>
        </Grid>

        {/* 운영 액션 */}
        <Grid item xs={12} md={7}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h2" mb={2}>운영 단계</Typography>
            <Stack direction="row" spacing={1} mb={3} flexWrap="wrap" useFlexGap>
              {STEPS.map((step) => (
                <Chip key={step.key} size="small" label={step.label}
                  color={step.done(s) ? 'success' : 'default'}
                  variant={step.done(s) ? 'filled' : 'outlined'} sx={{ fontWeight: 700 }} />
              ))}
            </Stack>

            <Stack spacing={2}>
              <Stack direction="row" spacing={1} alignItems="center">
                <Button variant="contained" disabled={s.status !== 'OPEN' || close.isPending}
                  onClick={() => close.mutate()}>① 청약 마감</Button>
                <Typography variant="caption" color="text.secondary">
                  {s.status === 'OPEN'
                    ? '목표 금액 달성·청약 기간 종료 시 자동으로 마감되며, 이 버튼으로 즉시 마감할 수도 있습니다.'
                    : '마감되었습니다.'}
                </Typography>
              </Stack>

              <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                <Button variant="contained" disabled={s.status === 'OPEN' || !!s.tokenContract || !isAdmin}
                  onClick={deploy}>② 컨트랙트 배포 + agent 부여</Button>
                {/* 권한 경계를 눈에 보이게 — 이 단계만 관리자 전용이다 */}
                <Chip size="small" color={isAdmin ? 'primary' : 'default'} variant={isAdmin ? 'filled' : 'outlined'}
                  label="관리자 전용" sx={{ fontWeight: 700 }} />
                <Typography variant="caption" color="text.secondary">
                  {isAdmin
                    ? 'TokenSecurity 를 배포하고 운영키·발행자에 agent 를 부여합니다.'
                    : '관리자 계정으로 로그인해야 실행할 수 있습니다.'}
                </Typography>
              </Stack>

              <Stack direction="row" spacing={1} alignItems="center">
                <Button variant="contained" disabled={!s.tokenContract || !!s.issueTxHash}
                  onClick={issue}>③ 발행·분배 (batchMint)</Button>
                <Typography variant="caption" color="text.secondary">
                  적격 청약자에게 배정 수량을 일괄 발행합니다.
                </Typography>
              </Stack>

              <Divider />

              <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                <TextField label="정산액(KRWS, 미입력=전액)" size="small" value={settleAmount}
                  onChange={(e) => setSettleAmount(e.target.value)} type="number" sx={{ width: 220 }} />
                {/* 발행 전 정산은 대금만 빠져나가므로 막는다(미달·취소는 환불로 처리) */}
                <Button variant="contained" color="success"
                  disabled={!s.issueTxHash || s.status === 'SETTLED'} onClick={settle}>
                  ④ 정산 (에스크로 → 발행자)
                </Button>
                {!s.issueTxHash && s.status !== 'OPEN' && (
                  <Typography variant="caption" color="text.secondary">발행(③) 완료 후 정산할 수 있습니다.</Typography>
                )}
              </Stack>

              <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                <TextField label="환불 지갑(미입력=전체)" size="small" value={refundWallet}
                  onChange={(e) => setRefundWallet(e.target.value)} placeholder="0x…" sx={{ width: 320 }} />
                <Button variant="outlined" color="warning" disabled={s.status === 'SETTLED'}
                  onClick={() => {
                    const w = refundWallet.trim() || undefined;
                    if (confirm(w ? `${w} 예치금을 환불합니다.` : '잔여 예치금 전체를 환불합니다. 진행할까요?')) {
                      void refund(w)();
                    }
                  }}>환불</Button>
                <Typography variant="caption" color="text.secondary">
                  미배정·취소분을 청약자에게 반환합니다(정산 완료 후 불가).
                </Typography>
              </Stack>
            </Stack>
          </Paper>
        </Grid>

        {/* 청약자 목록 */}
        <Grid item xs={12}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h2" mb={2}>청약자 목록</Typography>
            {settlement.isLoading && <LinearProgress />}
            {settlement.error && <Alert severity="warning">청약자 현황을 불러오지 못했습니다.</Alert>}
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>지갑</TableCell>
                  <TableCell align="right">청약 수량</TableCell>
                  <TableCell align="right">예치액(KRWS)</TableCell>
                  <TableCell>상태</TableCell>
                  <TableCell align="right">조치</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(st?.applicants ?? []).map((a) => (
                  <TableRow key={a.applicantUid}>
                    <TableCell><AddressWithCopy address={a.wallet} /></TableCell>
                    <TableCell align="right">{fmtUnits(a.amount, 0)}</TableCell>
                    <TableCell align="right">{fmtUnits(a.deposited)}</TableCell>
                    <TableCell>{a.status}</TableCell>
                    <TableCell align="right">
                      <Button size="small" color="warning"
                        disabled={!a.wallet || a.deposited === '0' || s.status === 'SETTLED'}
                        onClick={() => {
                          if (a.wallet && confirm(`${a.wallet} 예치금을 환불합니다.`)) void refund(a.wallet)();
                        }}>환불</Button>
                    </TableCell>
                  </TableRow>
                ))}
                {st && st.applicants.length === 0 && (
                  <TableRow><TableCell colSpan={5} align="center" sx={{ py: 3, color: 'text.secondary' }}>
                    청약자가 없습니다.
                  </TableCell></TableRow>
                )}
              </TableBody>
            </Table>
          </Paper>
        </Grid>
      </Grid>

      <TxProgressDialog open={txOpen} title={txTitle} pending={pending} result={result} error={error}
        onClose={() => setTxOpen(false)} plan={plan} steps={steps} />
    </Box>
  );
}

function Row({ k, v }: { k: string; v: React.ReactNode }) {
  return (
    <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ py: 0.75 }}>
      <Typography variant="body2" color="text.secondary">{k}</Typography>
      <Typography variant="body2" component="div" fontWeight={600}>{v}</Typography>
    </Stack>
  );
}

/** 마감 사유 — 어느 조건으로 닫혔는지 보여준다(RFP: 목표 달성/기간 종료/수동 종료) */
function CloseReasonChip({ reason, at }: { reason?: string | null; at?: string | null }) {
  const label = reason === 'TARGET_REACHED' ? '목표 금액 달성 (자동)'
    : reason === 'PERIOD_ENDED' ? '청약 기간 종료 (자동)'
    : reason === 'MANUAL' ? '수동 종료'
    : '기록 없음';
  const color = reason === 'TARGET_REACHED' ? 'success' : reason === 'PERIOD_ENDED' ? 'info' : 'default';
  return (
    <Stack direction="row" spacing={1} alignItems="center">
      <Chip size="small" color={color as 'success' | 'info' | 'default'} label={label} sx={{ fontWeight: 700 }} />
      {at && <Typography variant="caption" color="text.secondary">{new Date(at).toLocaleString('ko-KR')}</Typography>}
    </Stack>
  );
}
